<script setup lang="ts">
import { explainMonsterAffix } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType, NianMonsterType, PersonMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import NianMonsterRank from './NianMonsterRank.vue'
import { useCommonStore } from '@/stores/modules/common'
import { computed } from 'vue'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()
const commonStore = useCommonStore()

const props = defineProps<{ monster?: NianMonsterType; fightCount: number }>()
const emits = defineEmits(['start'])

const monsterCurHpPercent = computed(() => ((props.monster?.curHp || 0) / (props.monster?.hp || 0)) * 100)

function handleStartGoldTemple() {
  if (!props.fightCount) return ElMessage.warning('今日已参与过年兽共斗')
  ElMessageBox.confirm(`<p class="affix">确定进入【年兽共斗】挑战吗？（进入即<span class="shrink">消耗</span>挑战次数）</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync<PersonMonsterType>({ type: 'startNianMonsterFight' })
      emits('start')
    })
    .catch(() => {})
}

const rankShow = ref(false)
</script>

<template>
  <div class="border-wrap gold-tower">
    <h4 class="special">
      共斗年兽
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>共斗年兽说明</h4>
            <p>1. 新春期间，年兽<span class="info">每周日0点刷新</span>，需全服团结将其击败。</p>
            <p>2. 您拥有最多<span class="info">3分钟</span>的时间打击年兽，战斗时支持<span class="info">切换装备即刻生效</span>。</p>
            <p>3. 挑战次数每日0点刷新。</p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!monster">
        <template #reference>
          <el-button type="danger" plain style="margin-bottom: 5px">
            <span v-show="monster">👹{{ monster?.name }}👹</span>
            <span v-show="!monster">年兽已消灭</span>
          </el-button>
        </template>
        <div class="monster-detail">
          <h3 style="margin: 5px 0">{{ monster?.name }}</h3>
          <p>血量：{{ monster?.hp }}</p>
          <p>防御：{{ monster?.def }}</p>
          <p>闪避率：{{ monster?.evs }}%</p>
          <p>抗爆率：{{ monster?.crtdef }}%</p>
        </div>
      </el-popover>
    </p>
    <p style="display: flex; align-items: center" v-if="monster">
      <span>血量：</span>
      <span style="flex: 1">
        <el-progress :text-inside="true" :stroke-width="20" :percentage="(monsterCurHpPercent < 0 ? 0 : monsterCurHpPercent) || 0" status="exception">
          <span>{{ monster && monster.curHp ? monsterCurHpPercent.toFixed(2) : 0 }} %</span>
        </el-progress>
      </span>
    </p>
    <p>
      <span>剩余挑战次数：{{ fightCount }}</span>
    </p>
    <div style="margin: 10px 0">
      <el-button type="success" plain @click="handleStartGoldTemple()">开始战斗</el-button>
      <el-button type="primary" plain @click="rankShow = true">排行</el-button>
    </div>
  </div>

  <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
    <NianMonsterRank :monsterHp="monster?.hp || 0" />
    <template #footer>
      <p style="text-align: center">
        <el-button plain @click="rankShow = false" size="small">关闭</el-button>
      </p>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.gold-tower {
  flex-flow: column;
}
</style>
